import React, {Fragment, useEffect, useState} from "react";
import {Popover, Transition} from "@headlessui/react";
import {t} from "i18next";
import {Link} from "react-router-dom";
import {FlyoutOthersMenu} from "@/components/FlyoutOthersMenu";
import {FlyoutAboutMenu} from "@/components/FlyoutAboutMenu";
import {AccountContext} from "@/context/AccountProvider";
import Cookies from "universal-cookie";

export const FlyoutNavigationMenu = () => {
	const [isFlyoutNavigationMenuOpen, setIsFlyoutNavigationMenuOpen] = useState(false);
	const [isFlyoutNavigationMenuButtonHovered, setIsFlyoutNavigationMenuButtonHovered] = useState(false);

	const [isFlyoutAboutMenuOpen, setIsFlyoutAboutMenuOpen] = useState(false);
	const [isFlyoutOthersMenuOpen, setIsFlyoutOthersMenuOpen] = useState(false);

	const {cookie, account, isAuthenticated} = React.useContext(AccountContext);

	useEffect(() => {
		if (isFlyoutNavigationMenuOpen || isFlyoutAboutMenuOpen || isFlyoutOthersMenuOpen) {
			// Lock scrolling on the background
			document.body.style.overflow = 'hidden';
			document.body.style.height = '100dvh';
		} else {
			// Restore scrolling when the menu is closed
			document.body.style.overflow = '';
			document.body.style.height = '';
		}

		// Cleanup on component unmount
		return () => {
			document.body.style.overflow = '';
			document.body.style.height = '';
		};
	}, [isFlyoutNavigationMenuOpen, isFlyoutAboutMenuOpen, isFlyoutOthersMenuOpen]);

	return (
		<div
			className={`portrait:jk:hidden landscape:md:hidden h-fit py-[0.15rem] px-[0.35rem]
                ${isFlyoutNavigationMenuButtonHovered && 'bg-black/30'} 
                rounded-2xl flex items-center justify-center
                transition ease-in-out duration-150`}
		>
			<Popover>
				{({}) => {
					// To prevent the body from scrolling when the popover is isFlyoutNavigationMenuOpen
					document.body.classList.toggle(
						"overflow-y-hidden",
						isFlyoutNavigationMenuOpen || isFlyoutAboutMenuOpen || isFlyoutOthersMenuOpen
					);

					return (
						<>
							<button
								onMouseEnter={() => setIsFlyoutNavigationMenuButtonHovered(true)}
								onMouseLeave={() => setIsFlyoutNavigationMenuButtonHovered(false)}
								onClick={() => setIsFlyoutNavigationMenuOpen(!isFlyoutNavigationMenuOpen)}
								className="flex items-center outline-none
                                w-[1.25rem] portrait:sm:w-[2rem] landscape:w-[1.1rem] aspect-square"
							>
								<img
									className="h-full aspect-square"
									src="/assets/menu-icon.svg"
									alt="Menu"
								/>
							</button>

							<Transition
								show={isFlyoutNavigationMenuOpen}
								as={Fragment}
								enter="transition ease-out duration-300"
								enterFrom="opacity-0 translate-y-full"
								enterTo="opacity-100 translate-y-0"
								leave="transition ease-in duration-200"
								leaveFrom="opacity-100 translate-y-0"
								leaveTo="opacity-0 translate-y-full"
							>
								<Popover.Panel
									className="fixed z-[10000] flex flex-col bottom-0 left-0
                                    min-w-screen w-screen max-w-screen
                                    min-h-[100dvh] h-[100dvh] max-h-[100dvh]"
								>
									<div
										className="flex flex-col items-center justify-center
                                        h-full w-full bg-white overflow-hidden"
									>
										{/* Header */}
										<div
											className="w-full flex items-center justify-between
                                            portrait:py-[0.75rem] portrait:pl-[1.25rem] portrait:pr-[0.75rem]
                                            portrait:sm:py-[0.75rem] portrait:sm:pl-[1.25rem] portrait:sm:pr-[0.75rem]
                                            portrait:gap-x-[0.5rem] portrait:sm:gap-x-[0.75rem]
                                            landscape:py-[0.75rem] landscape:pl-[1rem] landscape:pr-[0.75rem]
                                            landscape:gap-x-[0.5rem] landscape:sm:gap-x-[0.75rem]
                                            relative after:content-[''] after:absolute
                                            after:right-0 after:bottom-0 after:left-0 after:h-[1px] after:bg-[length:17px_1px]
                                            after:bg-[linear-gradient(90deg,#EEEEE2,#EEEEE2_50%,transparent_0,transparent)]"
										>
											{/* Logo */}
											<Link
												to="/home"
												key="logo"
												className="flex items-center
                                                    portrait:h-[2rem] portrait:sm:h-[2.5rem]
                                                    landscape:h-[2rem] landscape:sm:h-[2.25rem]
                                                    gap-x-[0.5rem] portrait:sm:gap-x-[0.75rem]"
											>
												<img
													className="h-full aspect-square"
													src="/other_imgs/logo-joglekar kul-72x72.png"
													alt="Joglekarkul Logo"
												/>
												<h1
													key="logo-name"
													className="flex font-custom-bold
                                                    portrait:text-[1.25rem] portrait:sm:text-[1.6rem]
                                                    landscape:text-[1.25rem] landscape:sm:text-[1.4rem]"
												>
													{t("HEADER.JOGLEKARKUL")}
												</h1>
											</Link>

											{/* Close Icon */}
											<button
												onClick={() => setIsFlyoutNavigationMenuOpen(false)}
												type="button"
												className="flex items-center justify-center rounded-lg
                                                    portrait:h-[2rem] portrait:sm:h-[2.5rem]
                                                    landscape:h-[1.5rem] landscape:sm:h-[2.25rem]
                                                    aspect-square bg-transparent hover:bg-gray-200"
											>
												<img
													className={"h-[85%] aspect-square"}
													src={"/assets/close-icon-dark.svg"}
													alt={"Close"}
													title={"Close"}
												/>
											</button>
										</div>

										{/* Navigation Links */}
										<div
											className="h-full w-full flex-1 flex flex-col overflow-y-scroll
                                            portrait:py-[0.75rem] portrait:pl-[1.25rem] portrait:pr-[1rem]
                                            landscape:py-[0.5rem] landscape:pl-[1.25rem] landscape:pr-[1rem]"
										>
											<FlyoutAboutMenu
												isFlyoutAboutMenuOpen={isFlyoutAboutMenuOpen}
												setIsFlyoutAboutMenuOpen={setIsFlyoutAboutMenuOpen}
												setIsFlyoutNavigationMenuOpen={setIsFlyoutNavigationMenuOpen}
											/>

											<button
												onClick={() => {
													if (!isAuthenticated) {
														new Cookies().set("authRequired", true, {path: '/'});
														window.open("/login", "_self");
													} else {
														window.open("/kulavruksh", "_self");
													}
												}}
												key="Kulavruksh"
												className="w-full flex items-center py-[0.75rem]
                                                    font-custom-semi-bold text-gray-900
                                                    portrait:text-[1.1rem] portrait:sm:text-[1.3rem]
                                                    landscape:text-[1.15rem] landscape:sm:text-[1.25rem]
                                                    relative after:content-[''] after:absolute
                                                    after:right-0 after:bottom-0 after:left-0
                                                    after:h-[1.5px] after:bg-[length:10px_1px]
                                                    after:bg-[linear-gradient(90deg,#EEEEE2,#EEEEE2_50%,transparent_0,transparent)]"
											>
												<p className="line-clamp-1">
													{t("HEADER.KULAVRUKSH")}
												</p>
											</button>

											{/* Yellow Page */}
											<button
												onClick={() => {
													if (!isAuthenticated) {
														new Cookies().set("authRequired", true, {path: '/'});
														window.open('/login', '_self');
													} else {
														window.open("/yellow-page", '_self');
													}
												}}
												key="YellowPagesHomePage"
												className="w-full flex items-center py-[0.75rem]
                                                    font-custom-semi-bold text-gray-900
                                                    portrait:text-[1.1rem] portrait:sm:text-[1.3rem]
                                                    landscape:text-[1.15rem] landscape:sm:text-[1.25rem]
                                                    relative after:content-[''] after:absolute
                                                    after:right-0 after:bottom-0 after:left-0
                                                    after:h-[1.5px] after:bg-[length:10px_1px]
                                                    after:bg-[linear-gradient(90deg,#EEEEE2,#EEEEE2_50%,transparent_0,transparent)]"
											>
												<p className="line-clamp-1">
													{t("HEADER.YELLOW_PAGE")}
												</p>
											</button>

											{/* News & Events */}
											<button
												onClick={() => {
													if (!isAuthenticated) {
														new Cookies().set("authRequired", true, {path: '/'});
														window.open('/login', '_self');
													} else {
														window.open("/news-and-events", '_self');
													}
												}}
												key="News / Events"
												className="w-full flex items-center py-[0.75rem]
											        font-custom-semi-bold text-gray-900
											        portrait:text-[1.1rem] portrait:sm:text-[1.3rem]
											        landscape:text-[1.15rem] landscape:sm:text-[1.25rem]
											        relative after:content-[''] after:absolute
											        after:right-0 after:bottom-0 after:left-0
											        after:h-[1.5px] after:bg-[length:10px_1px]
											        after:bg-[linear-gradient(90deg,#EEEEE2,#EEEEE2_50%,transparent_0,transparent)]"
											>
												<p className="line-clamp-1">
													{t("HEADER.OTHER_MENU.NEWS_EVENTS.TITLE")}
												</p>
											</button>

											{/* Help Each Other */}
											{/*<button*/}
											{/*	onClick={() => {*/}
											{/*		if (!isAuthenticated) {*/}
											{/*			new Cookies().set("authRequired", true, {path: '/'});*/}
											{/*			window.open('/login', '_self');*/}
											{/*		} else {*/}
											{/*			window.open("/help-each-other", '_self');*/}
											{/*		}*/}
											{/*	}}*/}
											{/*	key="Help Each Other"*/}
											{/*	className="w-full flex items-center py-[0.75rem]*/}
											{/*        font-custom-semi-bold text-gray-900*/}
											{/*        portrait:text-[1.1rem] portrait:sm:text-[1.3rem]*/}
											{/*        landscape:text-[1.15rem] landscape:sm:text-[1.25rem]*/}
											{/*        relative after:content-[''] after:absolute*/}
											{/*        after:right-0 after:bottom-0 after:left-0*/}
											{/*        after:h-[1.5px] after:bg-[length:10px_1px]*/}
											{/*        after:bg-[linear-gradient(90deg,#EEEEE2,#EEEEE2_50%,transparent_0,transparent)]"*/}
											{/*>*/}
											{/*	<p className="line-clamp-1">*/}
											{/*		{t("HEADER.HELP_EACH_OTHER")}*/}
											{/*	</p>*/}
											{/*</button>*/}

											<FlyoutOthersMenu
												isFlyoutOthersMenuOpen={isFlyoutOthersMenuOpen}
												setIsFlyoutOthersMenuOpen={setIsFlyoutOthersMenuOpen}
												setIsFlyoutNavigationMenuOpen={setIsFlyoutNavigationMenuOpen}
											/>
										</div>

										{/* Footer */}
									</div>
								</Popover.Panel>
							</Transition>
						</>
					);
				}}
			</Popover>
		</div>
	)
}
